// src/hooks/useAdminAuth.js
// ─────────────────────────────────────────────────────────────────────────────
// HOW TO SET YOUR PASSWORD:
//   1. Open browser console on any page
//   2. Run: crypto.subtle.digest('SHA-256', new TextEncoder().encode('yourpassword'))
//        .then(b => console.log([...new Uint8Array(b)].map(x=>x.toString(16).padStart(2,'0')).join('')))
//   3. Copy the hash and paste it as ADMIN_HASH below
//
// Default password is: harsh@admin2025
// Change it by following the steps above with your own password
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from "react";

// SHA-256 of "harsh@admin2025" — change this to your own hash
const ADMIN_HASH = "3f285f508fede7a9c1eb950a95df2e511858f73d70b13d62259d8b17d6cceac2";

const SESSION_KEY = "portfolio_admin_session";
const SESSION_TTL = 4 * 60 * 60 * 1000; // 4 hours

const hashPassword = async (pw) => {
    const buf = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(pw)
    );
    return [...new Uint8Array(buf)]
        .map((x) => x.toString(16).padStart(2, "0"))
        .join("");
};

const isSessionValid = () => {
    try {
        const s = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
        return s && Date.now() - s.ts < SESSION_TTL;
    } catch { return false; }
};

export const useAdminAuth = () => {
    const [isAdmin, setIsAdmin] = useState(isSessionValid);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const login = useCallback(async (password) => {
        setLoading(true);
        setError(null);
        try {
            const hash = await hashPassword(password);
            if (hash === ADMIN_HASH) {
                sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ts: Date.now() }));
                setIsAdmin(true);
                return true;
            } else {
                setError("Incorrect password.");
                return false;
            }
        } catch {
            setError("Something went wrong.");
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(() => {
        sessionStorage.removeItem(SESSION_KEY);
        setIsAdmin(false);
    }, []);

    return { isAdmin, login, logout, error, loading };
};

// ── HOW TO GET YOUR OWN HASH ─────────────────────────────────────────────────
// Run this in the browser console to get the hash of any password:
//
// async function getHash(pw) {
//   const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pw));
//   return [...new Uint8Array(buf)].map(x=>x.toString(16).padStart(2,'0')).join('');
// }
// getHash('yourpassword').then(console.log);